/**
 * Ranking the symbol universe against what somebody has typed.
 *
 * Moved here from `components/SymbolDialog.tsx`, unchanged, when a second
 * surface needed the same answer: the screener's Add row offers the same
 * completions under a list as the search dialog does, and two rankings would be
 * two answers to "what did they mean by `ms`". `SymbolDialog` re-exports both
 * names, so nothing that imported them from there had to move.
 *
 * Pure — no React, no fetch — which is what lets `lib/` import it.
 */

import type { SymbolRow } from "./symbol-index";

/**
 * How many rows the dialog ever puts in the DOM — never the ~14,000 that a
 * one-letter query matches.
 *
 * There is no virtualiser here and there should not be one for a list nobody
 * scrolls past the top of: 300 rows is roughly ten screens, well under the point
 * where layout becomes visible, and past it the answer is a better query rather
 * than more scrolling. **The user is always told when this bites** — the footer
 * says so — because a list that silently stops at 300 reads as "that symbol does
 * not exist".
 */
export const MAX_ROWS = 300;

/**
 * Rank the universe against what has been typed, the way people type a ticker.
 *
 * Four tiers, in the order a person means them: an exact ticker, a ticker
 * prefix, a company-name prefix, then a substring of either. Tiers matter more
 * than any within-tier score, because they encode intent — someone typing `BA`
 * wants Boeing, not the first alphabetical company with "ba" buried in its name.
 *
 * **Within a tier the incoming order is kept**, which is the whole reason the
 * service sends the rows market-cap descending: `A` then surfaces AAPL and AMZN
 * above a micro-cap for free, with no score to tune and nothing here that has to
 * know what a market cap is.
 *
 * Each bucket stops growing at `limit`. Buckets are concatenated in tier order,
 * so an entry beyond the limit of its own bucket could never reach the rendered
 * list — capping as we go keeps a one-character query from materialising
 * thousands of rows it will immediately discard. The scan itself is not cut
 * short: an exact match can appear anywhere in the list, and stopping early
 * would drop it.
 */
export function rankSymbols(
  rows: readonly SymbolRow[],
  query: string,
  limit: number = MAX_ROWS,
): SymbolRow[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];

  const exact: SymbolRow[] = [];
  const tickerPrefix: SymbolRow[] = [];
  const namePrefix: SymbolRow[] = [];
  const substring: SymbolRow[] = [];
  const push = (bucket: SymbolRow[], row: SymbolRow) => {
    if (bucket.length < limit) bucket.push(row);
  };

  for (const row of rows) {
    const symbol = row.symbol.toLowerCase();
    const name = row.name?.toLowerCase() ?? "";
    if (symbol === needle) push(exact, row);
    else if (symbol.startsWith(needle)) push(tickerPrefix, row);
    else if (name.startsWith(needle)) push(namePrefix, row);
    else if (symbol.includes(needle) || name.includes(needle)) push(substring, row);
  }

  return [...exact, ...tickerPrefix, ...namePrefix, ...substring].slice(0, limit);
}
